import { memo, useCallback, useState, type CSSProperties } from "react";
import {
  ArrowLeftRight,
  Check,
  Copy,
  ExternalLink,
  FolderPlus,
  Globe,
  Lightbulb,
  Play,
  Plus,
  Sparkles,
} from "lucide-react";
import { PlateMedia } from "@/app/routes/dashboard/components/Plate";
import PlatformGlyph from "@/app/routes/dashboard/components/PlatformGlyph";
import { addedByClient, type Reference } from "@/types/reference.type";
import { posterFrameUrl } from "@/app/routes/dashboard/content-calendar/videoThumbnails";
import { cn } from "@/utils/helpers";
import { durationLabel, metaLine, TYPE_LABEL } from "./referenceMeta";

/*
 * References / Tile (CreativeOS Library.lib.pen, style B). Four faces:
 *  - Written why: the media at its own shape in a radius 12 frame, and the
 *    caption on the canvas under it: the why first (13/500, ink), the machine
 *    title (12, muted), then the mono source line with its marks.
 *  - Suggested by Creo: the why slot carries Creo's draft with Accept, Edit and
 *    Dismiss until a person decides.
 *  - Why missing: "Add why it's here" in the accent for whoever can write it,
 *    "No note yet" for everyone else.
 *  - Plate (Figma 06 · The wall, Tile · Plate): an idea someone typed has no
 *    media, so it is a card of its own: IDEA, the headline in Barlow, a rule,
 *    then the why.
 * Any other reference with no picture still gets a frame: the type name on the
 * surface, so the hover bar stays on the media and the why stays under it.
 * Hovering a tile raises the hover bar: Recreate, Collect and Open.
 */

// Measure actual media, never infer orientation from its platform or content type.
const RATIO_MIN = 0.35;
const RATIO_MAX = 3;

export interface ReferenceTileActions {
  onOpen: (id: string) => void;
  /** Open the detail with the why editor ready. */
  onAddWhy?: (id: string) => void;
  onRecreate?: (reference: Reference) => void;
  onCollect?: (reference: Reference, anchor: HTMLElement) => void;
  onSuggestion?: (reference: Reference, decision: "accept" | "dismiss") => void;
  onToggleSelect?: (id: string) => void;
}

function Marks({ reference }: { reference: Reference }) {
  return (
    <>
      {reference.brandId === null ? <span className="ref-tag">Everyone</span> : null}
      {addedByClient(reference) ? (
        <span
          className="ref-tag"
          title={reference.createdBy?.name ? `Added by ${reference.createdBy.name}` : "Added by the client"}
        >
          Client
        </span>
      ) : null}
    </>
  );
}

function Why({
  reference,
  canWrite,
  actions,
}: {
  reference: Reference;
  canWrite: boolean;
  actions: ReferenceTileActions;
}) {
  if (reference.userNote) return <p className="ref-why">{reference.userNote}</p>;
  if (reference.suggestedNote && canWrite) {
    return (
      <div className="ref-suggested">
        <span className="ref-suggested-label">
          <Sparkles aria-hidden className="size-3 text-co-accent" />
          <span><span className="normal-case">Creo</span> suggests a why</span>
        </span>
        <p className="ref-suggested-text">{reference.suggestedNote}</p>
        <span className="ref-suggested-actions">
          <button type="button" className="text-co-accent-ink" onClick={() => actions.onSuggestion?.(reference, "accept")}>
            Accept
          </button>
          <button type="button" className="text-co-text2" onClick={() => actions.onAddWhy?.(reference.id)}>
            Edit
          </button>
          <button type="button" className="text-co-muted" onClick={() => actions.onSuggestion?.(reference, "dismiss")}>
            Dismiss
          </button>
        </span>
      </div>
    );
  }
  if (canWrite && actions.onAddWhy) {
    return (
      <button type="button" className="ref-add-why" onClick={() => actions.onAddWhy?.(reference.id)}>
        <Plus aria-hidden className="size-[13px]" />
        Add why it's here
      </button>
    );
  }
  return <p className="ref-why ref-why--empty">No note yet</p>;
}

function HoverBar({ reference, actions }: { reference: Reference; actions: ReferenceTileActions }) {
  return (
    <div className="ref-hover-bar" role="group" aria-label={`Actions for ${reference.title}`}>
      {actions.onRecreate ? (
        <>
          <button type="button" className="ref-hover-action" onClick={() => actions.onRecreate?.(reference)}>
            <ArrowLeftRight aria-hidden className="size-[13px]" />
            Recreate
          </button>
          <span className="ref-hover-divider" aria-hidden />
        </>
      ) : null}
      {actions.onCollect ? (
        <>
          <button type="button" className="ref-hover-action" onClick={(e) => actions.onCollect?.(reference, e.currentTarget)}>
            <FolderPlus aria-hidden className="size-[13px]" />
            Collect
          </button>
          <span className="ref-hover-divider" aria-hidden />
        </>
      ) : null}
      <button type="button" className="ref-hover-action" onClick={() => actions.onOpen(reference.id)}>
        <ExternalLink aria-hidden className="size-[13px]" />
        Open
      </button>
    </div>
  );
}

function ReferenceTile({
  reference,
  canWrite,
  selected = false,
  ...actions
}: {
  reference: Reference;
  /** May write this reference's why (and act on M7's suggestion). */
  canWrite: boolean;
  selected?: boolean;
} & ReferenceTileActions) {
  const picture = reference.imageUrl ?? reference.thumbnailUrl;
  const hasRealMedia = Boolean(reference.videoUrl || picture);
  const isVideo = reference.type === "video" || Boolean(reference.videoUrl);
  const duration = reference.metrics?.durationMs;
  const slides = reference.carousel?.total ?? 0;
  const [ratio, setRatio] = useState<number | null>(null);
  const onNaturalSize = useCallback((width: number, height: number) => {
    if (width <= 0 || height <= 0) return;
    const next = Math.round(Math.min(RATIO_MAX, Math.max(RATIO_MIN, width / height)) * 1000) / 1000;
    setRatio((previous) => (previous === next ? previous : next));
  }, []);

  const open = () => actions.onOpen(reference.id);

  const check = actions.onToggleSelect ? (
    <button
      type="button"
      className={cn("ref-check", selected && "ref-check--on")}
      aria-pressed={selected}
      aria-label={`${selected ? "Deselect" : "Select"} ${reference.title}`}
      onClick={() => actions.onToggleSelect?.(reference.id)}
    >
      {selected ? <Check className="size-3.5" strokeWidth={3} /> : null}
    </button>
  ) : null;

  if (reference.type === "note" && !hasRealMedia) {
    return (
      <article className={cn("ref-cell", selected && "ref-cell--selected")} data-reference-id={reference.id}>
        <div className="ref-plate">
          <span className="ref-plate-meta">
            <Lightbulb aria-hidden className="size-[11px]" />
            {TYPE_LABEL.note}
          </span>
          <h3 className="ref-plate-headline">{reference.title}</h3>
          <span className="ref-plate-rule" aria-hidden />
          <div className="ref-caption ref-plate-caption">
            <Why reference={reference} canWrite={canWrite} actions={actions} />
            {reference.brandId === null || addedByClient(reference) ? (
              <p className="ref-meta">
                <Marks reference={reference} />
              </p>
            ) : null}
          </div>
          {check}
          <button
            type="button"
            className="ref-hit"
            onClick={open}
            aria-label={`Open idea: ${reference.title}${reference.userNote ? `. Saved because ${reference.userNote}` : ""}`}
          />
          <HoverBar reference={reference} actions={actions} />
        </div>
      </article>
    );
  }

  return (
    <article className={cn("ref-cell", selected && "ref-cell--selected")} data-reference-id={reference.id}>
      <div
        className="ref-media co-wall-media"
        style={ratio === null ? undefined : ({ "--co-wall-ratio": ratio } as CSSProperties)}
      >
        {hasRealMedia ? (
          <PlateMedia
            videoUrl={reference.videoUrl ? posterFrameUrl(reference.videoUrl) : null}
            thumbnailUrl={picture}
            hint="Open it for the source"
            onNaturalSize={onNaturalSize}
          />
        ) : (
          <div className="ref-placeholder" aria-hidden>
            <span className="ref-placeholder-label">{TYPE_LABEL[reference.type]}</span>
          </div>
        )}
        {isVideo && reference.videoUrl ? (
          <span className="ref-media-marks" aria-hidden>
            <span className="ref-play-mark">
              <Play className="size-3" fill="currentColor" />
            </span>
            {typeof duration === "number" && Number.isFinite(duration) && duration > 0 ? (
              <span className="ref-media-chip">{durationLabel(duration)}</span>
            ) : null}
          </span>
        ) : null}
        {slides > 1 ? (
          <span className="ref-media-chip ref-slides" aria-label={`${slides} slides`}>
            <Copy aria-hidden className="size-2.5" />
            {slides}
          </span>
        ) : null}
        {check}
        <button
          type="button"
          className="ref-hit"
          onClick={open}
          aria-label={`Open ${TYPE_LABEL[reference.type].toLowerCase()}: ${reference.title}${reference.userNote ? `. Saved because ${reference.userNote}` : ""}`}
        />
        <HoverBar reference={reference} actions={actions} />
      </div>
      <div className="ref-caption">
        <Why reference={reference} canWrite={canWrite} actions={actions} />
        <h3 className="ref-title">{reference.title}</h3>
        <p className="ref-meta">
          {reference.platform === "web" ? (
            <Globe aria-hidden className="size-3.5 text-co-muted" />
          ) : reference.platform ? (
            <PlatformGlyph platform={reference.platform.toUpperCase()} className="size-3.5 text-co-muted" />
          ) : null}
          {metaLine(reference)}
          <Marks reference={reference} />
        </p>
      </div>
    </article>
  );
}

export default memo(ReferenceTile);
